import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Printer, Loader2 } from 'lucide-react'
import { apiGet, cachedGet } from '../lib/api.js'
import { statusTone, STATUS_TONES } from '../data/patientStatuses.js'
import { STATUS_TH, codeCls } from '../data/appointmentStatuses.js'
import { nameInitial } from '../lib/text.js'
import { scaleStatus } from '../lib/scale.js'
import ScaleTag, { ScaleLegend } from '../components/ScaleTag.jsx'
import BrandMark from '../components/BrandMark.jsx'
import PlanChips from '../components/huddle/PlanChips.jsx'
import { planKey, planOutcome, groupSold } from '../lib/huddle.js'
import { useHuddleStaff } from '../lib/useHuddleStaff.js'

/**
 * Standalone, print-optimised single-doctor day schedule (A4 portrait).
 * Opened in a new tab from the appointment day-view table:
 *   /appointments/print?branch_id=&date=YYYY-MM-DD&doctor_id=[&upsell=0]
 *
 * The morning-huddle upsell plan is printed alongside each appointment (names
 * only — never the pitch note), so the sheet on the counter says what the team
 * agreed to offer. `upsell=0` prints a clean copy without it: this page ends up
 * as paper on a counter a patient can read.
 * Rendered outside the app Layout (no sidebar) so it prints clean. Same auth
 * cookie + same /api/appointments?type=dashboard call as the calendar, so the
 * ขูดหินปูน recall flag (<ScaleTag>) is the same one the day table shows.
 * Typography is deliberately one notch below the on-screen table — the sheet is
 * read at arm's length on a counter, and more rows per A4 page is worth more
 * than bigger type.
 */
const BKK = 7 * 3600 * 1000
const pad = (n) => String(n).padStart(2, '0')
const asList = (r) => (Array.isArray(r) ? r : (r?.items || []))
const bkkMin = (ms) => { const d = new Date(Number(ms) + BKK); return d.getUTCHours() * 60 + d.getUTCMinutes() }
const hm = (m) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`
const TH_MONTHS_FULL = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม']
const TH_DAYS_FULL = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']

// Status labels + badge colours: src/data/appointmentStatuses.js.

function thaiDate(dateStr) {
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(dateStr || ''))
  if (!m) return dateStr || '—'
  const [, y, mo, d] = m.map(Number)
  const dt = new Date(y, mo - 1, d)
  return `${TH_DAYS_FULL[dt.getDay()]} ${d} ${TH_MONTHS_FULL[mo - 1]} ${y + 543}`
}

const PRINT_CSS = `
@page { size: A4 portrait; margin: 10mm; }
/* PlanChips is sized for the screen; the sheet runs it a notch smaller. */
.plan-dense > span { font-size: 10px !important; padding: 0 3px !important; line-height: 1.5 !important; }
.plan-dense { gap: 2px !important; }
@media print {
  .no-print { display: none !important; }
  html, body { background: #fff !important; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .sheet { box-shadow: none !important; border: 0 !important; margin: 0 !important; padding: 0 !important; max-width: none !important; }
  thead { display: table-header-group; }   /* repeat header on each page */
  tr { break-inside: avoid; }
}
`

export default function AppointmentPrint() {
  const [sp, setSp] = useSearchParams()
  const branchId = sp.get('branch_id') || ''
  const date = sp.get('date') || ''
  const doctorId = sp.get('doctor_id') || ''
  const showUpsell = sp.get('upsell') !== '0'

  const [rows, setRows] = useState(null)
  const [branchName, setBranchName] = useState('')
  const [doctor, setDoctor] = useState(null) // { name, img }
  const [plans, setPlans] = useState([])
  const [sold, setSold] = useState([])
  const [promoNames, setPromoNames] = useState(new Map())

  useEffect(() => {
    document.title = `ตารางนัด ${date}`
    if (!branchId || !date) { setRows([]); return }
    apiGet('/api/appointments', { type: 'dashboard', branch_id: branchId, start_date: date, end_date: date })
      .then((r) => setRows(asList(r))).catch(() => setRows([]))
    cachedGet('/api/branches').then((b) => {
      const m = asList(b).find((x) => String(x.id ?? x.branch_id) === String(branchId))
      setBranchName(m?.name || m?.branch_name || '')
    }).catch(() => {})
    if (showUpsell) {
      apiGet('/api/huddle-upsell', { plan_date: date, branch_id: branchId })
        .then((r) => { setPlans(asList(r?.plans)); setSold(asList(r?.sold)) })
        .catch(() => {})
      cachedGet('/api/promotions')
        .then((r) => setPromoNames(new Map(asList(r).map((x) => [Number(x.id), x.Name || `#${x.id}`]))))
        .catch(() => {})
    }
    cachedGet('/api/doctors').then((d) => {
      const m = asList(d).find((x) => String(x.id) === String(doctorId))
      if (m) setDoctor({ name: m.code || [m.name, m.surname].filter(Boolean).join(' '), img: m.profile_img || '' })
    }).catch(() => {})
  }, [branchId, date, doctorId, showUpsell])

  const appts = useMemo(() => (rows || [])
    .filter((a) => !a.event_type && a.block !== true && String(a.doctor_id) === String(doctorId))
    .map((a) => {
      const p = a._patient || {}
      const s = bkkMin(a.start_date), e = bkkMin(a.end_date)
      return {
        s, e, dur: Number(a.duration) || Math.max(e - s, 0),
        patient: [p.name, p.nickname && `(${p.nickname})`].filter(Boolean).join(' ').trim() || (a.title || '').trim(),
        hn: p.HN || '',
        patientStatus: (p.status || '').trim(),
        scale: (Number(a.patient_id) || 0) > 0 ? scaleStatus(a) : null, // a.scale_date from the dashboard addon
        status: a.status || '',
        type: [(a._appointmentList?.type || '').trim(), (a._appointmentList?.detail || '').trim()].filter(Boolean).join(' · '),
        note: (a.details || '').trim(),
        remark: (a.remark || '').trim(),
        docName: a.doctor?.code || '',
        docImg: a.doctor?.profile_img || '',
        apptId: Number(a.id) || 0,
        patientId: Number(a.patient_id) || 0,
      }
    })
    .sort((x, y) => x.s - y.s), [rows, doctorId])

  const planFor = useMemo(() => {
    const m = new Map()
    for (const pl of plans) m.set(planKey(pl.appointment_id, pl.patient_id), pl)
    return m
  }, [plans])
  const soldIndex = useMemo(() => groupSold(sold), [sold])
  // ผู้รับผิดชอบ names — same roster the plan was assigned from.
  const staffNames = useHuddleStaff(showUpsell ? branchId : '', date)

  const confirmed = appts.filter((a) => a.status === 'C' || a.status === 'C*').length
  // On paper there is no tooltip, so the tooth glyph needs a key — but only when
  // a row actually carries one.
  const anyScale = appts.some((a) => a.scale?.overdue)
  const notConfirmed = appts.length - confirmed
  const docName = doctor?.name || appts[0]?.docName || `หมอ #${doctorId}`
  const docImg = doctor?.img || appts[0]?.docImg || ''

  const loading = rows === null

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4 print:bg-white print:p-0">
      <style>{PRINT_CSS}</style>

      {/* Toolbar — screen only */}
      <div className="no-print max-w-[794px] mx-auto mb-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="text-[13px] text-slate-500">ดูตัวอย่างก่อนพิมพ์ · A4 แนวตั้ง</div>
          {/* Paper on a counter is readable by patients — make leaving the
              upsell plan off a one-click choice. */}
          <label className="inline-flex items-center gap-1.5 text-[13px] text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showUpsell}
              onChange={(e) => {
                const next = new URLSearchParams(sp)
                if (e.target.checked) next.delete('upsell'); else next.set('upsell', '0')
                setSp(next, { replace: true })
              }}
              className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-200"
            />
            พิมพ์แผนอัปเซล
          </label>
        </div>
        <button type="button" onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 transition-colors">
          <Printer size={16} /> พิมพ์
        </button>
      </div>

      {/* A4 sheet (~794px @ 96dpi) */}
      <div className="sheet max-w-[794px] mx-auto bg-white rounded-lg shadow-lg print:shadow-none p-6 print:p-0">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 pb-2.5 border-b-2 border-slate-800">
          <div className="flex items-center gap-2.5">
            <BrandMark size={34} />
            <div>
              <div className="text-[16px] font-bold text-slate-900 leading-tight">Smile Seasons</div>
              <div className="text-[11px] text-slate-500 leading-tight">ตารางนัดหมายรายวัน</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[13px] font-semibold text-slate-900 leading-tight">{thaiDate(date)}</div>
            <div className="text-[11px] text-slate-600 leading-tight mt-0.5">สาขา {branchName || '—'}</div>
          </div>
        </div>

        {/* Doctor + counts */}
        <div className="flex items-center justify-between gap-4 py-2">
          <div className="flex items-center gap-2">
            {docImg
              ? <img src={docImg} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full object-cover ring-1 ring-slate-200" />
              : <div className="w-8 h-8 rounded-full bg-slate-100 ring-1 ring-slate-200 flex items-center justify-center text-[13px] font-semibold text-slate-600">{nameInitial(docName)}</div>}
            <div>
              <div className="text-[10px] text-slate-400 leading-tight">แพทย์</div>
              <div className="text-[14px] font-semibold text-slate-900 leading-tight">{docName}</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-[11px]">
            <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 font-medium text-slate-700">ทั้งหมด <b className="tabular-nums">{appts.length}</b></span>
            <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 ring-1 ring-emerald-200 px-2 py-1 font-medium text-emerald-700">ยืนยันแล้ว <b className="tabular-nums">{confirmed}</b></span>
            <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 ring-1 ring-amber-200 px-2 py-1 font-medium text-amber-700">ยังไม่ยืนยัน <b className="tabular-nums">{notConfirmed}</b></span>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-16 text-center text-slate-400 flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> กำลังโหลด…</div>
        ) : appts.length === 0 ? (
          <div className="py-16 text-center text-slate-400">ไม่มีนัดหมายในวันนี้</div>
        ) : (
          <table className="w-full text-[11.5px] border-collapse">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-300">
                <th className="py-1 pr-2 font-semibold w-[94px]">เวลา</th>
                <th className="py-1 px-2 font-semibold">คนไข้</th>
                <th className="py-1 px-2 font-semibold w-[96px]">สถานะคนไข้</th>
                <th className="py-1 px-2 font-semibold">รายละเอียด</th>
                <th className="py-1 pl-2 font-semibold text-center w-[44px]">สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {appts.map((a, i) => {
                const tone = a.patientStatus ? STATUS_TONES[statusTone(a.patientStatus)] : null
                const hasDetail = a.type || a.note || a.remark
                const plan = planFor.get(planKey(a.apptId, a.patientId))
                return (
                  <tr key={i} className="border-b border-slate-200 last:border-b-0 align-top">
                    {/* start–end: the range replaces the old duration sub-line, so the row is one line shorter. */}
                    <td className="py-1 pr-2 tabular-nums text-slate-800 font-medium whitespace-nowrap" title={`${a.dur} นาที`}>
                      {hm(a.s)} - {hm(a.e)}
                    </td>
                    <td className="py-1 px-2">
                      <div className="font-medium text-slate-900 leading-snug">
                        {a.patient || '—'}
                        {a.scale?.overdue && <ScaleTag status={a.scale} compact size={13} className="ml-1 align-middle" />}
                      </div>
                      {a.hn && <div className="text-[10px] text-slate-500 tabular-nums leading-tight">{a.hn}</div>}
                    </td>
                    <td className="py-1 px-2">
                      {tone
                        ? <span className={`inline-flex items-center gap-1 px-1 py-0 rounded-full text-[10px] font-medium ring-1 whitespace-nowrap ${tone.pill}`}>
                            <span className={`w-1 h-1 rounded-full ${tone.dot}`} />{a.patientStatus}
                          </span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-1 px-2">
                      {a.note && <div className="text-slate-800 leading-snug">{a.note}</div>}
                      {a.remark && <div className="text-[10.5px] text-slate-500 leading-snug">{a.remark}</div>}
                      {a.type && <div className="mt-0.5"><span className="inline-block rounded border border-slate-300 px-1 py-0 text-[9.5px] leading-[1.5] text-slate-600">{a.type}</span></div>}
                      {!hasDetail && !plan && <span className="text-slate-300">—</span>}
                      {plan && (
                        <PlanChips
                          className="plan-dense mt-0.5"
                          ids={plan.promotion_id || []}
                          hits={planOutcome(plan, soldIndex).hits}
                          promoNames={promoNames}
                          assignees={plan.assignee_id || []}
                          staffNames={staffNames}
                        />
                      )}
                    </td>
                    <td className="py-1 pl-2 text-center">
                      {a.status
                        ? <span title={STATUS_TH[a.status] || a.status}
                            className={`inline-flex items-center justify-center min-w-[24px] px-1 py-0 rounded text-[10.5px] font-bold ring-1 ${codeCls(a.status)}`}>{a.status}</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {/* Footer */}
        <div className="mt-3 pt-2 border-t border-slate-200 flex items-center justify-between gap-3 text-[10px] text-slate-400">
          <span className="whitespace-nowrap">Smile Seasons · ตารางนัดหมาย</span>
          {anyScale && <ScaleLegend />}
          <span className="whitespace-nowrap">พิมพ์เมื่อ {thaiDate(date)}</span>
        </div>
      </div>
    </div>
  )
}
